/* ============================================================
   THE CANINE CARPENTER — GSAP ANIMATIONS v10
   ─────────────────────────────────────────────────────────────
   Upgrades from v9:
   • ScrollSmoother (GSAP native — replaces Lenis)
   • Page loader — gold curtain wipe on every page arrival
   • Gold curtain page transitions on internal navigation
   • Hero: stretched entrance timing + video panel clip-wipe
   • Collections: alternating y-direction entry + deeper parallax
   • Featured products: arc-settle entrance + 3D mouse tilt
   • Bespoke band: expo.inOut wipe ease (one character upgrade)
   • Testimonials: group stagger cascade + star fill animation
   • Instagram grid: diagonal wave entry
   • Manifesto: CRAFT watermark scrubs with scroll
   • About story: marquee speed ties to scroll velocity
   • Custom cursor (desktop / pointer:fine only)
   ─────────────────────────────────────────────────────────────
   v10.1 additions:
   • Hero scroll-exit parallax (content drifts up, video drifts down)
   • Product card curtain-wipe entry (clip-path, editorial feel)
   • About/Story pinned image swap (image holds while chapters scroll)
   • Stat bars scrubbed to scroll position
   ─────────────────────────────────────────────────────────────
   Dependencies: gsap, ScrollTrigger, SplitText (existing CDN)
   New (free since Apr 2025): ScrollSmoother — added to theme.liquid CDN block
   No new paid plugins required.
   ============================================================ */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────
     SHARED STATE
  ───────────────────────────────────────────────────────── */
  var hasSplitText   = false;
  var smoother       = null;
  var isMobile       = window.innerWidth < 768;
  var isTablet       = window.innerWidth < 1024;
  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Post-loader queue — functions registered here fire after
     the loader wipes away. If no loader exists they fire immediately. */
  var postLoaderQueue = [];
  var loaderDone = false;
  function afterLoader(fn) {
    if (loaderDone) { fn(); }
    else { postLoaderQueue.push(fn); }
  }
  function flushPostLoader() {
    loaderDone = true;
    postLoaderQueue.forEach(function(fn) { fn(); });
    postLoaderQueue = [];
    /* All post-loader functions have now run their gsap.set() initial states.
       Add tcc-hero-ready so the CSS opacity:0 guard lifts — GSAP owns visibility now. */
    requestAnimationFrame(function() {
      document.body.classList.add('tcc-hero-ready');
    });
  }
  var ALWAYS_VISIBLE_SEL = [
    '[data-hero-eyebrow]','[data-hero-headline]','[data-hero-rule]',
    '[data-hero-sub]','[data-hero-actions]','[data-hero-actions] a',
    '[data-trust-item]','[data-story-reveal]',
    '[data-mission-quote-p]','[data-mission-quote-line]','[data-mission-body]'
  ].join(',');

  function revealAll() {
    document.querySelectorAll(ALWAYS_VISIBLE_SEL).forEach(function(el) {
      el.style.opacity  = '1';
      el.style.transform = 'none';
    });
  }

  /* ─────────────────────────────────────────────────────────
     1. SCROLL SMOOTHER
     Free as of April 2025 (GSAP + Webflow). Loaded from CDN.
     Requires #smooth-wrapper > #smooth-content in theme.liquid.
     Uses native scroll under the hood — no fake scrollbars,
     no touch/pointer conflicts.
     Degrades silently if plugin not present.
  ───────────────────────────────────────────────────────── */
  function initScrollSmoother() {
    if (typeof ScrollSmoother === 'undefined') {
      console.warn('[TCC] ScrollSmoother not found — using native scroll');
      return;
    }
    if (prefersReduced) return;

    var wrapper = document.getElementById('smooth-wrapper');
    var content = document.getElementById('smooth-content');
    if (!wrapper || !content) {
      console.warn('[TCC] ScrollSmoother: #smooth-wrapper / #smooth-content not found');
      return;
    }

    gsap.registerPlugin(ScrollSmoother);

    smoother = ScrollSmoother.create({
      wrapper:    '#smooth-wrapper',
      content:    '#smooth-content',
      smooth:      1.4,
      effects:     true,
      smoothTouch: 0.1
    });

    console.log('[TCC] ScrollSmoother active ✓');
  }

  /* ─────────────────────────────────────────────────────────
     2. PAGE LOADER
     Gold overlay (#tcc-loader) wipes up to reveal each page.
     The element is injected by theme.liquid.
  ───────────────────────────────────────────────────────── */
  function initPageLoader() {
    var loader = document.getElementById('tcc-loader');

    /* ── Shared helper: clear loader and reveal page content.
       Adding tcc-loaded to body triggers CSS: visibility:visible
       and hides the loader via body.tcc-loaded #tcc-loader{display:none} */
    function clearLoaderNow() {
      if (loader) {
        loader.style.display = 'none';
        loader.style.transform = '';
      }
      document.documentElement.classList.add('tcc-loaded');
      document.body.style.overflow = '';
      document.body.style.visibility = 'visible';
      document.body.classList.add('tcc-loaded');
      document.body.classList.add('tcc-hero-ready');
      loaderDone = true;
    }

    /* ── bfcache guard: on swipe-back the browser may restore a frozen
       snapshot. body.visibility is still 'hidden' from the initial load
       (before GSAP set it visible). Restore it immediately so the page
       isn't stuck invisible. Only fires on genuine cache restores. */
    window.addEventListener('pageshow', function(e) {
      if (e.persisted) {
        clearLoaderNow();
      }
    });

    if (!loader || prefersReduced) {
      if (loader) gsap.set(loader, { display: 'none' });
      document.body.classList.add('tcc-loaded');
      document.body.style.background = '';
      flushPostLoader();
      return;
    }

    var logoEl  = document.getElementById('tcc-loader-logo');
    var tccEl   = document.getElementById('tcc-loader-tcc');
    var nameEl  = document.getElementById('tcc-loader-name');
    var ringEl  = document.getElementById('tcc-loader-ring');

    /* Loader is already visible via CSS (display:flex from page load).
       Just ensure position is reset and lock scroll. */
    gsap.set(loader, { yPercent: 0 });
    document.body.style.overflow = 'hidden';

    /* ── Entrance sequence ──────────────────────────────────
       Wait for fonts before animating so TCC/name text never flashes invisible.
       document.fonts.ready resolves immediately if fonts are cached.
    ───────────────────────────────────────────────────────── */
    function runLoaderAnim() {
      var tl = gsap.timeline();

      if (logoEl) tl.to(logoEl, { opacity: 1, y: 0,  duration: 0.5,  ease: 'power3.out' }, 0.10);
      if (tccEl)  tl.to(tccEl,  { opacity: 1, y: 0,  duration: 0.5,  ease: 'power3.out' }, 0.25);
      if (nameEl) tl.to(nameEl, { opacity: 1,         duration: 0.4,  ease: 'power2.out' }, 0.42);
      if (ringEl) tl.to(ringEl, { opacity: 1,         duration: 0.35, ease: 'power2.out' }, 0.54);

    /* Hold while ring spins, then wipe the whole loader up */
      tl.to(loader, {
        yPercent:  -100,
        duration:   0.8,
        delay:      0.65,
        ease:       'expo.inOut',
        onStart: function() {
          /* Reveal the page body just as the curtain starts moving —
             the loader covers it fully at this point so no flash */
          document.body.style.visibility = 'visible';
          document.body.style.background = '';
        },
        onComplete: function() {
          document.body.style.overflow = '';
          document.body.classList.add('tcc-loaded');
          gsap.set(loader, { display: 'none' });
          ScrollTrigger.refresh();
          flushPostLoader();
        }
      });
    }

    if (document.fonts && document.fonts.ready) {
      /* Cap the font wait at 500ms so a slow CDN doesn't delay the loader */
      var fontTimeout = setTimeout(runLoaderAnim, 500);
      document.fonts.ready.then(function() {
        clearTimeout(fontTimeout);
        runLoaderAnim();
      });
    } else {
      runLoaderAnim();
    }

    console.log('[TCC] Page loader fired ✓');
  }

  /* ─────────────────────────────────────────────────────────
     3. GOLD CURTAIN PAGE TRANSITIONS
     Removed — the loader handles the full arrival experience.
     The curtain was causing a double-gold flash: curtain wipes
     down on click, then the loader fires on the new page, giving
     two sequential gold overlays. The loader alone is cleaner.
  ───────────────────────────────────────────────────────── */
  function initPageTransitions() {
    /* intentionally empty */
  }

  /* ─────────────────────────────────────────────────────────
     4. CUSTOM CURSOR  (desktop / fine pointer only)
  ───────────────────────────────────────────────────────── */

  /* ─────────────────────────────────────────────────────────
     MAIN INIT — all per-section animations
  ───────────────────────────────────────────────────────── */
  function init() {
    if (typeof gsap === 'undefined') {
      console.warn('[TCC] GSAP not loaded — skipping animations');
      revealAll();
      return;
    }

    console.log('[TCC] GSAP ' + gsap.version + ' loaded ✓');
    document.body.classList.add('gsap-ready');

    if (prefersReduced) { revealAll(); return; }

    if (typeof ScrollTrigger === 'undefined') {
      console.error('[TCC] ScrollTrigger not loaded');
      return;
    }
    gsap.registerPlugin(ScrollTrigger);

    hasSplitText = typeof SplitText !== 'undefined';
    if (hasSplitText) gsap.registerPlugin(SplitText);

    /* Hero entrance deferred to afterLoader — see initHeroEntrance() */
    afterLoader(initHeroEntrance);

    /* ══════════════════════════════════════════════════════
       SECTION HEADINGS — word reveal on scroll
    ══════════════════════════════════════════════════════ */
    document.querySelectorAll('.section-title').forEach(function(heading) {
      if (hasSplitText) {
        gsap.set(heading, { opacity: 1 });
        var s = new SplitText(heading, { type: 'words', wordsClass: 'split-word' });
        gsap.set(s.words, { opacity: 0, yPercent: 80 });
        ScrollTrigger.create({
          trigger: heading, start: 'top 90%', once: true,
          onEnter: function() {
            gsap.to(s.words, { opacity: 1, yPercent: 0, duration: 0.55, stagger: 0.07, ease: 'power3.out' });
          }
        });
      } else {
        gsap.set(heading, { opacity: 0, y: 20 });
        ScrollTrigger.create({
          trigger: heading, start: 'top 90%', once: true,
          onEnter: function() { gsap.to(heading, { opacity: 1, y: 0, duration: 0.6 }); }
        });
      }
    });

    /* ══════════════════════════════════════════════════════
       COLLECTION CARDS — Scroll-Spotlight  v4
       ─────────────────────────────────────────────────────────
       Dropped Flip entirely. Flip.getState uses getBoundingClientRect
       which returns wrong coords when ScrollSmoother has a CSS transform
       applied to #smooth-content — causing the instant snap bug.

       Instead: gsap.to() directly animates flexGrow on each card.
       One card tweens to flexGrow:4, all others tween to flexGrow:1.
       GSAP interpolates the numeric property — perfectly smooth,
       no coordinate system conflicts, no dependency on DOM positions.

       Scroll: section pins, progress maps to card index with snap.
       Info text: opacity 0→1 after flexGrow settles (delay 0.5s).
       Name chars: SplitText reveal fires after info fades in.
       Mobile: no pin, clip-path entry per card.
    ══════════════════════════════════════════════════════ */
    var collSection   = document.querySelector('[data-collections]');
    var collSpotlight = document.querySelector('[data-coll-spotlight]');

    if (collSection && collSpotlight) {
      var collCards  = collSpotlight.querySelectorAll('.coll-card');
      var totalCards = collCards.length;
      if (!totalCards) return;

      /* ── Shared: gold rule hover ── */
      collCards.forEach(function(card) {
        var rule = document.createElement('span');
        rule.setAttribute('aria-hidden', 'true');
        rule.style.cssText = [
          'position:absolute','bottom:0','left:0','width:100%','height:2px',
          'background:var(--gold,#c9a84c)','transform:scaleX(0)',
          'transform-origin:left center','display:block',
          'pointer-events:none','z-index:5'
        ].join(';');
        card.appendChild(rule);

        card.addEventListener('mouseenter', function() {
          if (window.innerWidth >= 768 && !card.classList.contains('coll-card--active')) return;
          gsap.to(rule, { scaleX: 1, duration: 0.45, ease: 'power3.out' });
        });
        card.addEventListener('mouseleave', function() {
          gsap.to(rule, { scaleX: 0, duration: 0.35, ease: 'power2.inOut', transformOrigin: 'right center' });
        });
      });

      /* ══════════════════════════════════════════════════
         DESKTOP — pinned scroll spotlight, no Flip
      ══════════════════════════════════════════════════ */
      if (window.innerWidth >= 768 && totalCards > 1) {

        var activeIdx    = 0;
        var ACTIVE_FLEX  = 4;
        var INACTIVE_FLEX = 1;
        var FLIP_DUR     = 0.75;
        var FLIP_EASE    = 'power2.inOut';

        /* Set all cards to use inline flex-grow so GSAP can tween it */
        collCards.forEach(function(card, i) {
          card.style.flexGrow = i === 0 ? ACTIVE_FLEX : INACTIVE_FLEX;
          card.style.flexShrink = '1';
          card.style.flexBasis = '0';

          /* Hide all info except first */
          var info = card.querySelector('.coll-info');
          if (info) info.style.opacity = i === 0 ? '1' : '0';

          /* ── Inject vertical label — desktop only ── */
          var nameEl = card.querySelector('.coll-name');
          if (nameEl && window.innerWidth >= 768) {
            var vLabel = document.createElement('div');
            vLabel.setAttribute('aria-hidden', 'true');
            vLabel.className = 'coll-vert-label';
            vLabel.textContent = nameEl.textContent.replace(/<[^>]*>/g, '').trim();
            vLabel.style.cssText = [
              'position:absolute',
              'bottom:50%',
              'left:50%',
              'transform:translateX(-50%) translateY(50%) rotate(180deg)',
              'writing-mode:vertical-rl',
              'text-orientation:mixed',
              'font-family:var(--heading,sans-serif)',
              'font-size:clamp(10px,1vw,13px)',
              'font-weight:700',
              'letter-spacing:0.18em',
              'text-transform:uppercase',
              'color:var(--off-white,#f0ece4)',
              'white-space:nowrap',
              'z-index:3',
              'pointer-events:none',
              'opacity:' + (i === 0 ? '0' : '0.75'),
              'transition:opacity 0.35s ease'
            ].join(';');
            card.appendChild(vLabel);
            card._vertLabel = vLabel;
          }

          /* Remove active class — flex handled by GSAP now */
          card.classList.remove('coll-card--active');
        });
        collCards[0].classList.add('coll-card--active');

        /* ── Name reveal ── */
        function revealName(card) {
          var nameEl = card.querySelector('.coll-name');
          if (!nameEl) return;
          gsap.killTweensOf(nameEl);

          if (hasSplitText) {
            if (nameEl._tccSplit) {
              try { nameEl._tccSplit.revert(); } catch(e) {}
            }
            gsap.set(nameEl, { opacity: 1 });
            var s = new SplitText(nameEl, { type: 'chars', charsClass: 'coll-char' });
            nameEl._tccSplit = s;
            s.chars.forEach(function(ch) {
              if (ch.parentNode && !ch.parentNode._tccMask) {
                var mask = document.createElement('span');
                mask.style.cssText = 'display:inline-block;overflow:hidden;vertical-align:bottom;';
                mask._tccMask = true;
                ch.parentNode.insertBefore(mask, ch);
                mask.appendChild(ch);
              }
            });
            gsap.fromTo(s.chars,
              { yPercent: 112 },
              { yPercent: 0, duration: 0.45, stagger: 0.014, ease: 'power3.out' }
            );
          } else {
            gsap.fromTo(nameEl, { opacity: 0 }, { opacity: 1, duration: 0.4 });
          }
        }

        /* Reveal first card name on load */
        revealName(collCards[0]);

        /* ── Spotlight: direct flexGrow tween, no Flip ── */
        function spotlightCard(idx) {
          if (idx === activeIdx || idx < 0 || idx >= totalCards) return;

          var prevIdx = activeIdx;
          activeIdx   = idx;

          collCards[prevIdx].classList.remove('coll-card--active');
          collCards[idx].classList.add('coll-card--active');

          /* Tween every card's flexGrow simultaneously */
          collCards.forEach(function(card, i) {
            gsap.to(card, {
              flexGrow: i === idx ? ACTIVE_FLEX : INACTIVE_FLEX,
              duration: FLIP_DUR,
              ease:     FLIP_EASE,
              overwrite: true
            });
          });

          /* Fade out old info, show its vertical label */
          var prevInfo  = collCards[prevIdx].querySelector('.coll-info');
          var nextInfo  = collCards[idx].querySelector('.coll-info');
          var prevLabel = collCards[prevIdx]._vertLabel;
          var nextLabel = collCards[idx]._vertLabel;

          if (prevInfo)  gsap.to(prevInfo,  { opacity: 0, duration: 0.2, overwrite: true });
          if (prevLabel) gsap.to(prevLabel, { opacity: 0.75, duration: 0.4, delay: 0.15, overwrite: true });
          if (nextLabel) gsap.to(nextLabel, { opacity: 0, duration: 0.2, overwrite: true });
          if (nextInfo) {
            gsap.to(nextInfo, {
              opacity:  1,
              duration: 0.35,
              delay:    0.45,
              ease:     'power2.out',
              overwrite: true,
              onStart:  function() { revealName(collCards[idx]); }
            });
          }
        }

        /* ── Pin + scroll-driven snap ── */
        var scrollPerCard = Math.round(window.innerHeight * 0.6);
        var totalScroll   = scrollPerCard * (totalCards - 1);

        ScrollTrigger.create({
          trigger:       collSection,
          start:         'top top',
          end:           '+=' + totalScroll,
          pin:           true,
          pinSpacing:    true,
          anticipatePin: 1,
          scrub:         1,
          snap: {
            snapTo:   function(val) {
              var n = totalCards - 1;
              return n > 0 ? Math.round(val * n) / n : 0;
            },
            duration: { min: 0.3, max: 0.65 },
            ease:     'power1.inOut',
            delay:    0.05
          },
          onUpdate: function(self) {
            var idx = Math.min(
              totalCards - 1,
              Math.max(0, Math.round(self.progress * (totalCards - 1)))
            );
            spotlightCard(idx);
          }
        });

      /* ══════════════════════════════════════════════════
         MOBILE — clip-path entry, no pin
      ══════════════════════════════════════════════════ */
      } else {
        collCards.forEach(function(card, i) {
          var imgWrap = card.querySelector('.coll-bg');
          var nameEl  = card.querySelector('.coll-name');
          var tagEl   = card.querySelector('.coll-tag');
          var ctaEl   = card.querySelector('.coll-cta');

          if (imgWrap) gsap.set(imgWrap, { clipPath: 'inset(0 0 100% 0)' });
          if (nameEl)  gsap.set(nameEl,  { opacity: 0, y: 16 });
          if (tagEl)   gsap.set(tagEl,   { opacity: 0 });
          if (ctaEl)   gsap.set(ctaEl,   { opacity: 0 });

          ScrollTrigger.create({
            trigger: card, start: 'top 88%', once: true,
            onEnter: function() {
              var tl = gsap.timeline({ delay: i * 0.07 });
              if (imgWrap) tl.to(imgWrap, { clipPath: 'inset(0 0 0% 0)', duration: 0.7, ease: 'expo.inOut' }, 0);
              if (nameEl)  tl.to(nameEl,  { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' }, 0.15);
              if (tagEl)   tl.to(tagEl,   { opacity: 1, duration: 0.35 }, 0.35);
              if (ctaEl)   tl.to(ctaEl,   { opacity: 1, duration: 0.3  }, 0.45);
            }
          });
        });
      }
    }

    /* ══════════════════════════════════════════════════════
       FEATURED PRODUCTS — Ghost number curtain
       ─────────────────────────────────────────────────────────
       Each card starts showing only a large ghost index number
       (01, 02, 03, 04) at low opacity — no image, no text visible.
       On scroll entry, the product image wipes UP from the bottom
       behind the number, literally replacing it. As the image
       settles the number fades to nothing. Product name rises from
       a mask, price + CTA fade in last. Each card fires on its own
       ScrollTrigger with a 120ms inter-card stagger.
       The number is injected by JS so no liquid changes needed.
    ══════════════════════════════════════════════════════ */
    var featSection = document.querySelector('.featured');
    if (featSection) {
      var prodCards = featSection.querySelectorAll('.prod-card');
      if (prodCards.length) {

        prodCards.forEach(function(card, i) {
          var imgWrap  = card.querySelector('.prod-img');
          var nameEl   = card.querySelector('.prod-name');
          var catEl    = card.querySelector('.prod-cat');
          var descEl   = card.querySelector('.prod-desc');
          var footerEl = card.querySelector('.prod-footer');

          /* ── Inject ghost number ── */
          var ghost = document.createElement('div');
          ghost.setAttribute('aria-hidden', 'true');
          ghost.textContent = (i + 1 < 10 ? '0' : '') + (i + 1);
          ghost.style.cssText = [
            'position:absolute',
            'inset:0',
            'display:flex',
            'align-items:center',
            'justify-content:center',
            'font-family:var(--heading,sans-serif)',
            'font-size:clamp(80px,14vw,180px)',
            'font-weight:700',
            'color:rgba(201,168,76,0.18)',
            'letter-spacing:-0.04em',
            'pointer-events:none',
            'z-index:3',
            'line-height:1',
            'user-select:none'
          ].join(';');
          /* Card needs relative positioning */
          card.style.position = 'relative';
          card.appendChild(ghost);

          /* ── Initial states ── */
          /* Image starts fully hidden behind the number */
          if (imgWrap) gsap.set(imgWrap, { clipPath: 'inset(0 0 100% 0)' });

          /* Name masked */
          if (nameEl) {
            var nameWrap = document.createElement('div');
            nameWrap.style.cssText = 'overflow:hidden;display:block;';
            nameEl.parentNode.insertBefore(nameWrap, nameEl);
            nameWrap.appendChild(nameEl);
            gsap.set(nameEl, { yPercent: 105 });
          }

          /* Info elements hidden */
          if (catEl)    gsap.set(catEl,    { opacity: 0 });
          if (descEl)   gsap.set(descEl,   { opacity: 0 });
          if (footerEl) gsap.set(footerEl, { opacity: 0, y: 10 });

          /* ── ScrollTrigger per card ── */
          ScrollTrigger.create({
            trigger: card,
            start:   'top 85%',
            once:    true,
            onEnter: function() {
              var delay = i * 0.12;
              var tl = gsap.timeline({ delay: delay });

              /* Beat 1 — image wipes up from behind the number (expo snap) */
              if (imgWrap) {
                tl.to(imgWrap, {
                  clipPath:  'inset(0 0 0% 0)',
                  duration:  0.85,
                  ease:      'expo.inOut'
                }, 0);
              }

              /* Beat 1b — number fades out as image arrives */
              tl.to(ghost, {
                opacity: 0,
                duration: 0.5,
                ease:    'power2.out'
              }, 0.35);

              /* Beat 2 — product name rises from mask */
              if (nameEl) {
                tl.to(nameEl, {
                  yPercent: 0,
                  duration: 0.55,
                  ease:     'power3.out'
                }, 0.55);
              }

              /* Beat 3 — category label */
              if (catEl) {
                tl.to(catEl, {
                  opacity:  1,
                  duration: 0.35,
                  ease:     'power2.out'
                }, 0.65);
              }

              /* Beat 4 — description */
              if (descEl) {
                tl.to(descEl, {
                  opacity:  1,
                  duration: 0.35,
                  ease:     'power2.out'
                }, 0.75);
              }

              /* Beat 5 — price + CTA settle in last */
              if (footerEl) {
                tl.to(footerEl, {
                  opacity:  1,
                  y:        0,
                  duration: 0.4,
                  ease:     'power2.out'
                }, 0.85);
              }
            }
          });
        });
      }
    }

    /* ══════════════════════════════════════════════════════
       BRAND STATEMENT — premium entrance
       Layout unchanged. Entrance upgraded:
       • Headline split to WORDS not lines — two sentences fire as
         separate beats. "Not a pet shop." slams in first, pause,
         "A craft workshop." slams in second. Felt as two statements.
       • expo.out on headline words — fast snap, not a gentle rise
       • CRAFT watermark scales down from 1.4 as it fades in — depth
       • Deco lines snap on expo.out not grow on power2.out
       • Rule draws immediately after headline, no long delay
       • Stats bounce in on back.out(1.7) — confident, not timid
    ══════════════════════════════════════════════════════ */
    var brandSection = document.querySelector('[data-brand-section]');
    if (brandSection) {
      var bEyebrow   = brandSection.querySelector('[data-brand-eyebrow]');
      var bEyeLine   = brandSection.querySelector('.brand-eyebrow__line');
      var bHeadline  = brandSection.querySelector('[data-brand-headline]');
      var bRule      = brandSection.querySelector('[data-brand-rule]');
      var bBody      = brandSection.querySelector('[data-brand-body]');
      var bCta       = brandSection.querySelector('[data-brand-cta]');
      var bStats     = brandSection.querySelectorAll('[data-brand-stat]');
      var bStatBars  = brandSection.querySelectorAll('.brand-stat__bar');
      var bStatNums  = brandSection.querySelectorAll('.brand-stat__num');
      var bDecoWord  = brandSection.querySelector('.brand-deco__word');
      var bDecoLines = brandSection.querySelectorAll('.brand-deco__line');

      /* Initial states */
      if (bEyebrow) gsap.set(bEyebrow, { opacity: 0, x: -24 });
      if (bBody)    gsap.set(bBody,    { opacity: 0, y: 20 });
      if (bCta)     gsap.set(bCta,     { opacity: 0, y: 14 });
      if (bRule)    gsap.set(bRule,    { width: 0, opacity: 0 });
      /* CRAFT starts large + invisible — scales DOWN into place */
      if (bDecoWord) gsap.set(bDecoWord, { opacity: 0, scale: 1.35, x: 40 });
      bDecoLines.forEach(function(l) { gsap.set(l, { scaleY: 0, transformOrigin: 'top center' }); });

      /* CRAFT watermark scrubs right→left while scrolling through section */
      if (bDecoWord && !isTablet) {
        gsap.to(bDecoWord, {
          x: -80, ease: 'none',
          scrollTrigger: {
            trigger: brandSection,
            start:   'top bottom',
            end:     'bottom top',
            scrub:   1.5
          }
        });
      }

      ScrollTrigger.create({
        trigger: brandSection, start: 'top 68%', once: true,
        onEnter: function() {
          var btl = gsap.timeline();

          /* Deco lines snap in — expo not power2 */
          btl.to(bDecoLines, {
            scaleY: 1, duration: 0.7, stagger: 0.1, ease: 'expo.out'
          }, 0);

          /* CRAFT scales down into place */
          if (bDecoWord) {
            btl.to(bDecoWord, {
              opacity: 1, scale: 1, x: 0,
              duration: 1.1, ease: 'power3.out'
            }, 0);
          }

          /* Eyebrow + rule snap in */
          if (bEyebrow) btl.to(bEyebrow, { opacity: 1, x: 0, duration: 0.4, ease: 'power3.out' }, 0.1);
          if (bEyeLine) btl.to(bEyeLine, { width: 36, duration: 0.5, ease: 'expo.out' }, 0.12);

          /* Headline — split to words, two sentences fire as separate beats */
          if (bHeadline && hasSplitText) {
            gsap.set(bHeadline, { opacity: 1 });
            var bs = new SplitText(bHeadline, { type: 'words', wordsClass: 'bsl-word' });

            /* Wrap each word in overflow:hidden mask */
            bs.words.forEach(function(w) {
              var mask = document.createElement('div');
              mask.style.cssText = 'overflow:hidden;display:inline-block;vertical-align:bottom;padding-bottom:0.05em;';
              w.parentNode.insertBefore(mask, w);
              mask.appendChild(w);
            });
            gsap.set(bs.words, { yPercent: 110 });

            /* Find the split point between the two sentences
               by looking for a word that follows a full stop */
            var sentence1 = [], sentence2 = [];
            var inSecond = false;
            bs.words.forEach(function(w) {
              if (inSecond) {
                sentence2.push(w);
              } else {
                sentence1.push(w);
                /* If this word ends with a full stop, next word starts sentence 2 */
                if (w.textContent.trim().slice(-1) === '.') inSecond = true;
              }
            });

            /* Sentence 1 — slam in */
            if (sentence1.length) {
              btl.to(sentence1, {
                yPercent: 0,
                duration: 0.5,
                stagger:  0.055,
                ease:     'expo.out'
              }, 0.2);
            }

            /* Beat — pause, then sentence 2 arrives as a second statement */
            if (sentence2.length) {
              btl.to(sentence2, {
                yPercent: 0,
                duration: 0.5,
                stagger:  0.055,
                ease:     'expo.out'
              }, sentence1.length ? 0.2 + sentence1.length * 0.055 + 0.25 : 0.5);
            }

          } else if (bHeadline) {
            gsap.set(bHeadline, { opacity: 0, y: 30 });
            btl.to(bHeadline, { opacity: 1, y: 0, duration: 0.8, ease: 'power4.out' }, 0.2);
          }

          /* Rule draws right after headline lands */
          if (bRule) {
            btl.to(bRule, {
              width: isMobile ? '40px' : 'clamp(40px,5vw,60px)',
              opacity: 1, duration: 0.5, ease: 'expo.out'
            }, 0.75);
          }

          /* Body */
          if (bBody) btl.to(bBody, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }, 0.85);

          /* CTA */
          if (bCta) btl.to(bCta, { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out' }, 0.98);

          /* Stats cascade with confident bounce */
          if (bStats.length) {
            btl.to(bStats, {
              opacity: 1, y: 0,
              duration: 0.5, stagger: 0.09, ease: 'back.out(1.7)'
            }, 0.8);

            /* Stat bars scrubbed */
            bStatBars.forEach(function(bar) {
              gsap.fromTo(bar,
                { width: '0px' },
                {
                  width: '32px', ease: 'none',
                  scrollTrigger: {
                    trigger: bar, start: 'top 85%', end: 'top 55%', scrub: 0.8
                  }
                }
              );
            });

            /* Stat numbers count up */
            bStatNums.forEach(function(numEl) {
              var raw     = numEl.getAttribute('data-count') || numEl.textContent;
              var numeric = parseFloat(raw.replace(/[^0-9.]/g, ''));
              var prefix  = raw.match(/^[^0-9]*/)[0];
              var suffix  = raw.match(/[^0-9.]*$/)[0];
              if (!isNaN(numeric) && numeric > 0 && numeric <= 9999) {
                var obj = { val: 0 };
                gsap.to(obj, {
                  val: numeric, duration: 1.4, delay: 0.9, ease: 'power2.out',
                  scrollTrigger: { trigger: numEl, start: 'top 90%', once: true },
                  onUpdate: function() {
                    numEl.textContent = prefix + (numeric % 1 === 0 ? Math.round(obj.val) : obj.val.toFixed(1)) + suffix;
                  }
                });
              }
            });
          }
        }
      });

      /* Also set stats initial state here — outside onEnter */
      if (bStats.length) gsap.set(bStats, { opacity: 0, y: 18 });
    }

    /* ══════════════════════════════════════════════════════
       BESPOKE BAND — expo.inOut wipe (was power3.inOut)
    ══════════════════════════════════════════════════════ */
    var bespokeBand = document.querySelector('[data-bespoke-band]');
    if (bespokeBand) {
      var bWipe     = bespokeBand.querySelector('.bespoke-bg-wipe');
      var bbEyebrow = bespokeBand.querySelector('[data-bespoke-eyebrow]');
      var bbHeadline = bespokeBand.querySelector('[data-bespoke-headline]');
      var bbCta     = bespokeBand.querySelector('[data-bespoke-cta]');
      var bbNote    = bespokeBand.querySelector('[data-bespoke-note]');

      if (bbEyebrow) gsap.set(bbEyebrow, { opacity: 0 });
      if (bbCta)     gsap.set(bbCta,     { opacity: 0, y: 12 });
      if (bbNote)    gsap.set(bbNote,    { opacity: 0 });

      var bbWords = [];
      if (bbHeadline && hasSplitText) {
        gsap.set(bbHeadline, { opacity: 1 });
        var bbSplit = new SplitText(bbHeadline, { type: 'words', wordsClass: 'bespoke-word' });
        bbWords = bbSplit.words;
        bbWords.forEach(function(w, i) { gsap.set(w, { opacity: 0, x: i % 2 === 0 ? -60 : 60 }); });
      } else if (bbHeadline) {
        gsap.set(bbHeadline, { opacity: 0, y: 20 });
      }

      ScrollTrigger.create({
        trigger: bespokeBand, start: 'top 80%', once: true,
        onEnter: function() {
          var tl = gsap.timeline({ defaults: { ease: 'power4.out' } });
          if (bWipe) tl.to(bWipe, { scaleX: 0, transformOrigin: 'left center', duration: 0.7, ease: 'expo.inOut' }, 0);
          if (bbEyebrow) tl.to(bbEyebrow, { opacity: 1, duration: 0.4 }, 0.3);
          if (bbWords.length) {
            tl.to(bbWords, { opacity: 1, x: 0, duration: 0.55, stagger: 0.07, ease: 'power4.out' }, 0.45);
          } else if (bbHeadline) {
            tl.to(bbHeadline, { opacity: 1, y: 0, duration: 0.6 }, 0.45);
          }
          if (bbCta)  tl.to(bbCta,  { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' }, 0.85);
          if (bbNote) tl.to(bbNote, { opacity: 1, duration: 0.5 }, 1.0);
        }
      });
    }

    /* ══════════════════════════════════════════════════════
       TESTIMONIALS — group stagger cascade + star fill
    ══════════════════════════════════════════════════════ */
    var reviewsGrid = document.querySelector('.reviews-grid');
    if (reviewsGrid) {
      var reviewCards = reviewsGrid.querySelectorAll('.review-card');
      if (reviewCards.length) {
        gsap.set(reviewCards, { opacity: 0, y: 30, rotation: -1 });

        ScrollTrigger.create({
          trigger: reviewsGrid, start: 'top 85%', once: true,
          onEnter: function() {
            /* Cards cascade left-to-right as a group */
            gsap.to(reviewCards, {
              opacity: 1, y: 0, rotation: 0,
              duration: 0.65, stagger: 0.1, ease: 'back.out(1.4)'
            });

            /* Stars fill left→right on each card as it arrives */
            reviewCards.forEach(function(card, i) {
              var stars = card.querySelector('.stars');
              if (!stars) return;

              var starText = stars.textContent.trim();
              stars.innerHTML =
                '<span class="stars-fill" style="display:inline-block;overflow:hidden;white-space:nowrap;clip-path:inset(0 100% 0 0)">' + starText + '</span>' +
                '<span aria-hidden="true" style="position:absolute;opacity:0.2;white-space:nowrap;top:0;left:0">' + starText + '</span>';
              stars.style.position = 'relative';

              gsap.to(stars.querySelector('.stars-fill'), {
                clipPath: 'inset(0 0% 0 0)',
                duration: 0.65,
                delay:    0.25 + i * 0.1,
                ease:     'power2.inOut'
              });
            });
          }
        });
      }
    } else {
      /* Fallback for isolated cards without the grid wrapper */
      document.querySelectorAll('.testi-card, .testimonial-item, .review-card').forEach(function(card) {
        gsap.set(card, { opacity: 0, scale: 0.95, y: 20 });
        ScrollTrigger.create({
          trigger: card, start: 'top 88%', once: true,
          onEnter: function() { gsap.to(card, { opacity: 1, scale: 1, y: 0, duration: 0.6, ease: 'back.out(1.4)' }); }
        });
      });
    }

    /* ══════════════════════════════════════════════════════
       INSTAGRAM GRID — diagonal wave entry
    ══════════════════════════════════════════════════════ */
    var instaSection = document.querySelector('.insta');
    if (instaSection) {
      var instaTiles = instaSection.querySelectorAll('.insta-thumb');
      if (instaTiles.length) {
        var firstTile = instaTiles[0];
        var cols = Math.round(instaSection.offsetWidth / (firstTile.offsetWidth || 1));
        cols = Math.max(1, Math.min(cols, 8));

        instaTiles.forEach(function(tile, idx) {
          var col = idx % cols;
          var row = Math.floor(idx / cols);
          gsap.set(tile, { opacity: 0, scale: 0.88, y: 16 });
          tile._diagDelay = (col + row) * 0.055;
        });

        ScrollTrigger.create({
          trigger: instaSection, start: 'top 85%', once: true,
          onEnter: function() {
            instaTiles.forEach(function(tile) {
              gsap.to(tile, {
                opacity: 1, scale: 1, y: 0,
                duration: 0.45, delay: tile._diagDelay, ease: 'power2.out'
              });
            });
          }
        });
      }
    }

    /* ══════════════════════════════════════════════════════
       COLLECTION PAGE — hero title char reveal
    ══════════════════════════════════════════════════════ */
    var collHeroTitle = document.querySelector('.collection-hero__title');
    if (collHeroTitle && hasSplitText) {
      var cSplit = new SplitText(collHeroTitle, { type: 'chars' });
      gsap.set(cSplit.chars, { opacity: 0, yPercent: 100 });
      gsap.to(cSplit.chars, {
        opacity: 1, yPercent: 0,
        duration: 0.5, stagger: 0.022, ease: 'power3.out', delay: 0.15
      });
    }

    /* ══════════════════════════════════════════════════════
       PRODUCT PAGE — entrance
    ══════════════════════════════════════════════════════ */
    var productCarousel = document.querySelector('.product-carousel');
    if (productCarousel) {
      gsap.from(productCarousel, { opacity: 0, scale: 0.97, duration: 0.6, ease: 'power2.out', delay: 0.1 });
      var productInfo = document.querySelector('.product-info');
      if (productInfo) {
        gsap.set(productInfo.children, { opacity: 0, y: 18 });
        gsap.to(productInfo.children, { opacity: 1, y: 0, duration: 0.5, stagger: 0.08, ease: 'power2.out', delay: 0.25 });
      }
    }

    window.addEventListener('load', function() {
      ScrollTrigger.refresh();
      console.log('[TCC] ScrollTrigger refreshed after load ✓');
    });

    console.log('[TCC] Animations initialised ✓');
  }

  /* ─────────────────────────────────────────────────────────
     ABOUT STORY PAGE
  ───────────────────────────────────────────────────────── */
  /* ─────────────────────────────────────────────────────────
     TCC JOURNAL v13 — "The Making Of"
     ─────────────────────────────────────────────────────────
     initJournalChapters()  — ghost numerals + chapter reveals
     initStoryReveal()      — entry point, also handles carousel + mission
  ───────────────────────────────────────────────────────── */

  /* ── Shared: word-slam heading ── */
  function storyWordSlam(el, triggerEl, delayOffset) {
    if (!el) return;
    if (!hasSplitText) {
      gsap.set(el, { opacity: 0, y: 24 });
      ScrollTrigger.create({
        trigger: triggerEl || el, start: 'top 88%', once: true,
        onEnter: function() { gsap.to(el, { opacity: 1, y: 0, duration: 0.7, delay: delayOffset || 0, ease: 'power3.out' }); }
      });
      return;
    }
    gsap.set(el, { opacity: 1 });
    var s = new SplitText(el, { type: 'words', wordsClass: 'sw-word' });
    s.words.forEach(function(w) {
      var mask = document.createElement('span');
      mask.style.cssText = 'display:inline-block;overflow:hidden;vertical-align:bottom;padding-bottom:0.05em;';
      w.parentNode.insertBefore(mask, w);
      mask.appendChild(w);
    });
    gsap.set(s.words, { yPercent: 112 });
    ScrollTrigger.create({
      trigger: triggerEl || el, start: 'top 86%', once: true,
      onEnter: function() {
        gsap.to(s.words, { yPercent: 0, duration: 0.65, stagger: 0.055, delay: delayOffset || 0, ease: 'expo.out' });
      }
    });
  }

  /* ═══════════════════════════════════════════════════════════
     JOURNAL — chapter intro strip
  ═══════════════════════════════════════════════════════════ */
  function initJournalIntro() {
    var intro = document.querySelector('[data-journal-intro]');
    if (!intro) return;
    var rules = intro.querySelectorAll('.tcj-intro__rule');
    var label = intro.querySelector('.tcj-intro__label');

    if (isMobile) {
      if (label) label.classList.add('mob-reveal');
      var mObs = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) return;
          if (label) setTimeout(function() { label.classList.add('mob-reveal--in'); }, 100);
          mObs.unobserve(intro);
        });
      }, { threshold: 0.3 });
      mObs.observe(intro);
      return;
    }
    gsap.set(label, { opacity: 0, y: 8 });
    ScrollTrigger.create({
      trigger: intro, start: 'top 90%', once: true,
      onEnter: function() {
        gsap.to(rules, { scaleX: 1, duration: 1.0, ease: 'expo.inOut', stagger: 0 });
        gsap.to(label, { opacity: 1, y: 0, duration: 0.6, delay: 0.3, ease: 'power3.out' });
      }
    });
  }

  /* ═══════════════════════════════════════════════════════════
     JOURNAL — chapter dividers (gold rule draws + word fades)
  ═══════════════════════════════════════════════════════════ */
  function initJournalDividers() {
    document.querySelectorAll('[data-ch-divider]').forEach(function(div) {
      var lines = div.querySelectorAll('.tcj-divider__line');
      var word  = div.querySelector('.tcj-divider__word');
      ScrollTrigger.create({
        trigger: div, start: 'top 92%', once: true,
        onEnter: function() {
          if (lines.length) gsap.to(lines, { scaleX: 1, duration: 0.8, ease: 'expo.inOut', stagger: 0.1 });
          if (word) gsap.to(word, { opacity: 0.4, duration: 0.5, delay: 0.3, ease: 'power2.out' });
        }
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════
     JOURNAL — chapters 1–3 (text + image reveals)
  ═══════════════════════════════════════════════════════════ */
  function initJournalChapters() {
    var chapters = document.querySelectorAll('[data-chapter]');
    if (!chapters.length) return;

    /* ── Mobile: lightweight IntersectionObserver fade-ins ── */
    if (isMobile) {
      var mObs = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) return;
          var ch = entry.target;
          var els = [
            ch.querySelector('[data-ch-meta]'),
            ch.querySelector('[data-ch-heading]'),
            ch.querySelector('[data-ch-body]'),
            ch.querySelector('[data-ch-img]'),
            ch.querySelector('[data-ch-stat]'),
            ch.querySelector('[data-ch-process]'),
            ch.querySelector('[data-ch-final]')
          ].filter(Boolean);
          els.forEach(function(el, i) {
            el.classList.add('mob-reveal');
            setTimeout(function() { el.classList.add('mob-reveal--in'); }, i * 80);
          });
          var values = ch.querySelectorAll('[data-value]');
          values.forEach(function(v, i) {
            v.classList.add('mob-reveal');
            setTimeout(function() { v.classList.add('mob-reveal--in'); }, 200 + i * 80);
          });
          mObs.unobserve(ch);
        });
      }, { threshold: 0.12 });
      chapters.forEach(function(ch) { mObs.observe(ch); });
      return;
    }

    chapters.forEach(function(ch) {
      var chNum    = ch.getAttribute('data-chapter');
      var ghost    = ch.querySelector('.tcj-chapter__ghost');
      var meta     = ch.querySelector('[data-ch-meta]');
      var heading  = ch.querySelector('[data-ch-heading]');
      var body     = ch.querySelector('[data-ch-body]');
      var imgBlock = ch.querySelector('[data-ch-img]');
      var img      = ch.querySelector('.tcj-img');
      var corners  = ch.querySelectorAll('.tcj-img-frame__corner');
      var imgLabel = ch.querySelector('.tcj-img-frame__label');
      var statRow  = ch.querySelector('[data-ch-stat]');
      var process  = ch.querySelector('[data-ch-process]');
      var values   = ch.querySelectorAll('[data-value]');
      var finalEl  = ch.querySelector('[data-ch-final]');

      /* Ghost numeral — parallax drift upward as section scrolls */
      if (ghost && chNum !== '5') {
        gsap.set(ghost, { opacity: 0 });
        ScrollTrigger.create({
          trigger: ch, start: 'top 80%', once: true,
          onEnter: function() { gsap.to(ghost, { opacity: 1, duration: 1.2, ease: 'power2.out' }); }
        });
        ScrollTrigger.create({
          trigger: ch, start: 'top bottom', end: 'bottom top', scrub: 1.5,
          onUpdate: function(self) {
            gsap.set(ghost, { y: self.progress * -60 });
          }
        });
      }

      /* Meta row */
      if (meta) {
        gsap.set(meta, { opacity: 0, x: -20 });
        ScrollTrigger.create({
          trigger: ch, start: 'top 80%', once: true,
          onEnter: function() { gsap.to(meta, { opacity: 1, x: 0, duration: 0.55, ease: 'power3.out' }); }
        });
      }

      /* Heading — word slam */
      if (heading && chNum !== '5') storyWordSlam(heading, ch, 0.15);

      /* Body */
      if (body) {
        ScrollTrigger.create({
          trigger: ch, start: 'top 76%', once: true,
          onEnter: function() { gsap.to(body, { opacity: 1, y: 0, duration: 0.7, delay: 0.3, ease: 'power3.out' }); }
        });
        gsap.set(body, { opacity: 0, y: 20 });
      }

      /* Image block — clip wipe from edge */
      if (imgBlock) {
        var isFlip = ch.classList.contains('tcj-chapter--flip');
        var clipStart = isFlip ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)';
        gsap.set(imgBlock, { opacity: 1 });
        gsap.set(imgBlock, { clipPath: clipStart });
        if (img) gsap.set(img, { scale: 1.07 });

        ScrollTrigger.create({
          trigger: ch, start: 'top 78%', once: true,
          onEnter: function() {
            gsap.to(imgBlock, { clipPath: 'inset(0 0% 0 0%)', duration: 1.0, ease: 'expo.inOut' });
            if (img) gsap.to(img, { scale: 1, duration: 2.2, ease: 'power2.out' });
            /* Corner accents pop */
            if (corners.length) {
              gsap.to(corners, { scale: 1, duration: 0.45, delay: 0.7, ease: 'back.out(2)', stagger: 0.08 });
            }
            if (imgLabel) gsap.to(imgLabel, { opacity: 1, duration: 0.4, delay: 0.8 });
          }
        });
      }

      /* Stats row */
      if (statRow) {
        ScrollTrigger.create({
          trigger: statRow, start: 'top 86%', once: true,
          onEnter: function() { gsap.to(statRow, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }); }
        });
        gsap.set(statRow, { opacity: 0, y: 16 });
      }

      /* Process steps */
      if (process) {
        var steps = process.querySelectorAll('.tcj-process__step');
        gsap.set(process, { opacity: 1 });
        ScrollTrigger.create({
          trigger: process, start: 'top 84%', once: true,
          onEnter: function() {
            steps.forEach(function(step, i) {
              var line = document.createElement('span');
              line.setAttribute('aria-hidden', 'true');
              line.style.cssText = 'position:absolute;left:0;top:0;bottom:0;width:2px;background:var(--gold);transform:scaleY(0);transform-origin:top center;border-radius:1px;display:block;';
              step.insertBefore(line, step.firstChild);
              gsap.from(step, { opacity: 0, x: -16, duration: 0.45, delay: i * 0.09, ease: 'power3.out' });
              gsap.to(line,  { scaleY: 1, duration: 0.45, delay: i * 0.09 + 0.15, ease: 'expo.out' });
            });
          }
        });
      }

      /* Values grid (chapter 3) */
      if (values.length) {
        var allPaths = [];
        values.forEach(function(v) {
          v.querySelectorAll('path,rect,circle').forEach(function(p) {
            var len = p.getTotalLength ? p.getTotalLength() : 60;
            p.style.strokeDasharray  = len;
            p.style.strokeDashoffset = len;
            allPaths.push(p);
          });
        });
        ScrollTrigger.create({
          trigger: ch.querySelector('[data-ch-values]') || ch, start: 'top 80%', once: true,
          onEnter: function() {
            if (allPaths.length) gsap.to(allPaths, { strokeDashoffset: 0, duration: 0.7, ease: 'power2.inOut', delay: 0.2 });
            gsap.to(values, { opacity: 1, y: 0, duration: 0.55, stagger: 0.1, delay: 0.25, ease: 'power3.out' });
          }
        });
        gsap.set(values, { opacity: 0, y: 28 });
      }

      /* Final CTA chapter */
      if (finalEl) {
        /* Ghost for chapter 5 */
        if (ghost) {
          gsap.set(ghost, { opacity: 0 });
          ScrollTrigger.create({
            trigger: ch, start: 'top 80%', once: true,
            onEnter: function() { gsap.to(ghost, { opacity: 1, duration: 1.4, ease: 'power2.out' }); }
          });
        }
        ScrollTrigger.create({
          trigger: ch, start: 'top 76%', once: true,
          onEnter: function() {
            var q = ch.querySelector('[data-ch-heading]');
            var btns = ch.querySelector('[data-ch-btns]');
            if (q) storyWordSlam(q, ch, 0);
            gsap.to(finalEl, { opacity: 1, duration: 0.01 }); /* make visible */
            if (btns) {
              var bs = btns.querySelectorAll('a');
              gsap.set(bs, { opacity: 0, y: 18 });
              gsap.to(bs, { opacity: 1, y: 0, duration: 0.55, stagger: 0.1, delay: 0.5, ease: 'back.out(1.5)' });
            }
          }
        });
      }
    });

    console.log('[TCC] Journal chapters active \u2713');
  }

  /* ═══════════════════════════════════════════════════════════
     MOBILE CAROUSEL — touch swipe
  ═══════════════════════════════════════════════════════════ */
  function initStoryCarousel() {
    var carousel = document.querySelector('[data-story-carousel]');
    if (!carousel || !isMobile) return;
    var track   = carousel.querySelector('[data-carousel-track]');
    var slides  = carousel.querySelectorAll('[data-carousel-slide]');
    var dots    = carousel.querySelectorAll('[data-carousel-dot]');
    var total   = slides.length;
    var current = 0;
    var startX  = 0;
    var dragging = false;

    function goTo(n) {
      current = Math.max(0, Math.min(total - 1, n));
      track.style.transform = 'translateX(-' + (current * 100) + '%)';
      dots.forEach(function(d, i) { d.classList.toggle('as-carousel__dot--active', i === current); });
    }
    dots.forEach(function(dot) {
      dot.addEventListener('click', function() { goTo(parseInt(dot.getAttribute('data-carousel-dot'), 10)); });
    });
    track.addEventListener('touchstart', function(e) { startX = e.touches[0].clientX; dragging = true; }, { passive: true });
    track.addEventListener('touchmove', function(e) {
      if (!dragging) return;
      track.style.transform = 'translateX(calc(-' + (current * 100) + '% + ' + (e.touches[0].clientX - startX) + 'px))';
    }, { passive: true });
    track.addEventListener('touchend', function(e) {
      if (!dragging) return; dragging = false;
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 50) goTo(dx < 0 ? current + 1 : current - 1); else goTo(current);
    });
    ScrollTrigger.create({
      trigger: carousel, start: 'top 88%', once: true,
      onEnter: function() { gsap.from(carousel, { opacity: 0, y: 30, duration: 0.6, ease: 'power3.out' }); }
    });
    console.log('[TCC] Story carousel active \u2713');
  }

  /* ═══════════════════════════════════════════════════════════
     STORY REVEAL — entry point
  ═══════════════════════════════════════════════════════════ */
  function initStoryReveal() {
    var journal = document.querySelector('[data-journal]');
    if (!journal) return;

    initJournalIntro();
    initJournalDividers();
    initJournalChapters();
    initStoryCarousel();
    initMissionSection();
  }

  function initMissionSection() {
    var section = document.querySelector('[data-mission-section]');
    if (!section) return;

    var label      = section.querySelector('[data-mission-label]');
    var ruleLeft   = section.querySelector('.as-mission__rule-left');
    var ruleRight  = section.querySelector('.as-mission__rule-right');
    var lines      = section.querySelectorAll('[data-mission-line]');
    var ulWords    = section.querySelectorAll('[data-mission-underline]');
    var verdict    = section.querySelector('[data-mission-verdict]');
    var verdictPlain = verdict ? verdict.querySelector('.as-mission__verdict-plain') : null;
    var verdictBrand = verdict ? verdict.querySelector('.as-mission__verdict-brand') : null;
    var divider    = section.querySelector('[data-mission-divider]');
    var body       = section.querySelector('[data-mission-body]');

    /* ── Mobile: simple CSS transition reveals ── */
    if (isMobile) {
      var mobEls = [label, body, verdictBrand].filter(Boolean);
      mobEls.forEach(function(el) { el.classList.add('mob-reveal'); });
      lines.forEach(function(line) {
        var inner = line.querySelector('.as-mission__line-inner');
        if (inner) inner.classList.add('mob-reveal');
      });
      if (verdict) {
        var vInner = verdict.querySelector('.as-mission__line-inner');
        if (vInner) vInner.classList.add('mob-reveal');
      }
      var mObs = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) return;
          mobEls.forEach(function(el, i) {
            setTimeout(function() { el.classList.add('mob-reveal--in'); }, i * 100);
          });
          lines.forEach(function(line, i) {
            var inner = line.querySelector('.as-mission__line-inner');
            if (inner) setTimeout(function() { inner.classList.add('mob-reveal--in'); }, 100 + i * 120);
          });
          if (verdict) {
            var vInner = verdict.querySelector('.as-mission__line-inner');
            if (vInner) setTimeout(function() { vInner.classList.add('mob-reveal--in'); }, 460);
          }
          mObs.unobserve(section);
        });
      }, { threshold: 0.1 });
      mObs.observe(section);
      return;
    }

    /* ── Reduced-motion fallback ── */
    if (prefersReduced) {
      ScrollTrigger.create({
        trigger: section, start: 'top 70%', once: true,
        onEnter: function() {
          gsap.to(section, { opacity: 1, duration: 0.4 });
        }
      });
      return;
    }

    /* ── Set initial states ── */
    if (label)        gsap.set(label,        { opacity: 0, y: 10 });
    if (ruleLeft)     gsap.set(ruleLeft,      { width: 0 });
    if (ruleRight)    gsap.set(ruleRight,     { width: 0 });

    /* Each line-inner starts translated down, clipped by .as-mission__line overflow:hidden */
    lines.forEach(function(line) {
      var inner = line.querySelector('.as-mission__line-inner');
      if (inner) gsap.set(inner, { y: '110%' });
    });

    /* Inject real underline bar spans — GSAP animates width 0 → 100% */
    ulWords.forEach(function(w) {
      var bar = document.createElement('span');
      bar.className = 'as-mission__underline-bar';
      w.style.position = 'relative';
      w.appendChild(bar);
      w._ulBar = bar;
    });

    if (verdict) {
      var vInner = verdict.querySelector('.as-mission__line-inner');
      if (vInner) gsap.set(vInner, { y: '110%' });
    }
    if (verdictBrand) gsap.set(verdictBrand, { opacity: 0, scale: 0.92, transformOrigin: 'left center' });
    if (divider)      gsap.set(divider,      { width: 0 });
    if (body)         gsap.set(body,         { opacity: 0, y: 20 });

    /* ── Build timeline ── */
    ScrollTrigger.create({
      trigger: section,
      start:   'top 85%',
      once:    true,
      onEnter: function() {
        var tl = gsap.timeline({ defaults: { ease: 'expo.out' } });

        /* T=0 — Rules draw + label fades */
        if (ruleLeft)  tl.to(ruleLeft,  { width: 48, duration: 0.7 }, 0);
        if (ruleRight) tl.to(ruleRight, { width: 48, duration: 0.7 }, 0);
        if (label)     tl.to(label,     { opacity: 1, y: 0, duration: 0.5 }, 0.1);

        /* T=0.35 — Line 1 */
        var l1 = lines[0] ? lines[0].querySelector('.as-mission__line-inner') : null;
        if (l1) tl.to(l1, { y: '0%', duration: 0.75 }, 0.35);

        /* T=0.55 — Line 2 + underline on "stylish" */
        var l2 = lines[1] ? lines[1].querySelector('.as-mission__line-inner') : null;
        if (l2) {
          tl.to(l2, { y: '0%', duration: 0.75 }, 0.55);
          var ul2 = lines[1].querySelector('[data-mission-underline]');
          if (ul2 && ul2._ulBar) {
            tl.to(ul2._ulBar, { width: '100%', duration: 0.5, ease: 'power2.inOut' }, 1.05);
          }
        }

        /* T=0.75 — Line 3 + underline on "dog-approved" */
        var l3 = lines[2] ? lines[2].querySelector('.as-mission__line-inner') : null;
        if (l3) {
          tl.to(l3, { y: '0%', duration: 0.75 }, 0.75);
          var ul3 = lines[2].querySelector('[data-mission-underline]');
          if (ul3 && ul3._ulBar) {
            tl.to(ul3._ulBar, { width: '100%', duration: 0.5, ease: 'power2.inOut' }, 1.28);
          }
        }

        /* T=1.15 — Verdict line rises */
        var vInner = verdict ? verdict.querySelector('.as-mission__line-inner') : null;
        if (vInner) tl.to(vInner, { y: '0%', duration: 0.8 }, 1.15);

        /* T=1.45 — Brand name stamps in */
        if (verdictBrand) {
          tl.to(verdictBrand, {
            opacity: 1, scale: 1, duration: 0.55, ease: 'back.out(1.4)'
          }, 1.45);
        }

        /* T=1.85 — Divider rule draws */
        if (divider) tl.to(divider, { width: 40, duration: 0.6, ease: 'power2.inOut' }, 1.85);

        /* T=2.05 — Body drifts up */
        if (body) {
          tl.to(body, {
            opacity: 1, y: 0, duration: 0.7, ease: 'power3.out'
          }, 2.05);
        }
      }
    });
  }

  /* ═══════════════════════════════════════════════════════════
     ABOUT HERO — entrance animation
     Heading, eyebrow, sub and scroll hint all start at opacity:0
     in CSS and are animated in after the page loader wipes away.
  ═══════════════════════════════════════════════════════════ */
  function initAboutHero() {
    var hero = document.querySelector('.about-hero');
    if (!hero) return;

    var eyebrow    = hero.querySelector('.about-hero__eyebrow');
    var heading    = hero.querySelector('.about-hero__heading');
    var sub        = hero.querySelector('.about-hero__sub');
    var scrollHint = hero.querySelector('.about-hero__scroll-hint');

    /* ── Mobile: simple staggered CSS reveal ── */
    if (isMobile) {
      [eyebrow, heading, sub, scrollHint].filter(Boolean).forEach(function(el) {
        el.classList.add('mob-reveal');
      });
      afterLoader(function() {
        setTimeout(function() {
          [eyebrow, heading, sub, scrollHint].filter(Boolean).forEach(function(el, i) {
            setTimeout(function() { el.classList.add('mob-reveal--in'); }, i * 120);
          });
        }, 100);
      });
      return;
    }

    /* Heading uses SplitText line-by-line slam if available */
    var heroSplit = null;
    if (heading && typeof SplitText !== 'undefined') {
      gsap.set(heading, { opacity: 1 });
      heroSplit = new SplitText(heading, { type: 'lines', linesClass: 'ah-split-line' });
      heroSplit.lines.forEach(function(line) {
        var wrap = document.createElement('div');
        wrap.style.cssText = 'overflow:hidden;display:block;';
        line.parentNode.insertBefore(wrap, line);
        wrap.appendChild(line);
      });
      gsap.set(heroSplit.lines, { yPercent: 110, opacity: 0 });
    }

    afterLoader(function() {
      var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      /* Eyebrow slides in from left */
      if (eyebrow) tl.to(eyebrow, { opacity: 1, x: 0, duration: 0.6 }, 0.3);

      /* Heading — split lines slam up through mask, or simple fade */
      if (heroSplit && heroSplit.lines.length) {
        tl.to(heroSplit.lines, {
          yPercent: 0, opacity: 1,
          duration: 0.85, stagger: 0.18,
          ease: 'power3.out'
        }, 0.5);
      } else if (heading) {
        tl.to(heading, { opacity: 1, y: 0, duration: 0.85 }, 0.5);
      }

      /* Sub fades up */
      if (sub) tl.to(sub, { opacity: 1, y: 0, duration: 0.6 }, 0.85);

      /* Scroll hint */
      if (scrollHint) tl.to(scrollHint, { opacity: 0.65, duration: 0.5 }, 1.1);
    });
  }

  /* ─────────────────────────────────────────────────────────
     BOOT SEQUENCE
  ───────────────────────────────────────────────────────── */

  /* ═══════════════════════════════════════════════════════════
     IMAGE TRAIL GALLERY
     Desktop: images appear at cursor position and fade out,
              each slightly rotated and staggered for organic feel.
     Mobile:  GSAP-powered swipe carousel — slides off-screen
              with momentum and snap.
  ═══════════════════════════════════════════════════════════ */
  function initImageTrail() {
    var section = document.querySelector('[data-img-trail]');
    if (!section) return;

    /* ── HEADING: two-beat word reveal ──
       "EXPLORE" slams in first, then "THE CRAFT" arrives
       as a second statement — same mechanic as manifesto */
    var heading = section.querySelector('[data-trail-heading]');
    if (heading && hasSplitText) {
      gsap.set(heading, { opacity: 1 });
      var hSplit = new SplitText(heading, { type: 'words', wordsClass: 'tw-word' });
      hSplit.words.forEach(function(w) {
        var mask = document.createElement('span');
        mask.style.cssText = 'display:inline-block;overflow:hidden;vertical-align:bottom;padding-bottom:0.05em;';
        w.parentNode.insertBefore(mask, w);
        mask.appendChild(w);
      });
      gsap.set(hSplit.words, { yPercent: 112 });

      /* Split into two beats at the natural break */
      var beat1 = [], beat2 = [];
      var inBeat2 = false;
      hSplit.words.forEach(function(w) {
        if (!inBeat2 && w.textContent.trim().toUpperCase() === 'THE') inBeat2 = true;
        if (inBeat2) beat2.push(w); else beat1.push(w);
      });

      ScrollTrigger.create({
        trigger: section, start: 'top 78%', once: true,
        onEnter: function() {
          /* Beat 1 — first word(s) slam in */
          if (beat1.length) {
            gsap.to(beat1, { yPercent: 0, duration: 0.55, stagger: 0.06, ease: 'expo.out' });
          }
          /* Beat 2 — "the craft" arrives after a pause */
          if (beat2.length) {
            gsap.to(beat2, {
              yPercent: 0, duration: 0.55, stagger: 0.06, ease: 'expo.out',
              delay: beat1.length ? beat1.length * 0.06 + 0.22 : 0.3
            });
          }
          /* Gold rule draws under heading */
          var rule = section.querySelector('.img-trail__rule');
          if (rule) gsap.to(rule, { scaleX: 1, duration: 0.6, delay: 0.7, ease: 'expo.out' });
          /* Subline fades up */
          var sub = section.querySelector('.img-trail__sub');
          if (sub) gsap.to(sub, { opacity: 1, y: 0, duration: 0.5, delay: 0.8, ease: 'power3.out' });
        }
      });
    } else if (heading) {
      gsap.set(heading, { opacity: 0, y: 20 });
      ScrollTrigger.create({
        trigger: section, start: 'top 78%', once: true,
        onEnter: function() { gsap.to(heading, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' }); }
      });
    }

    /* ── DESKTOP: mouse trail — desktop only ── */
    var canvas = section.querySelector('[data-trail-canvas]');
    if (canvas && !isMobile && !isTablet) {
      var items      = Array.from(canvas.querySelectorAll('[data-trail-item]'));
      var totalItems = items.length;

      /* If no images uploaded, skip trail silently */
      if (!totalItems) return;

      var activeIndex = 0;
      var lastX = -9999, lastY = -9999;
      var threshold = 80;
      var zCounter  = 9990;

      /* Move all trail items to body so they sit above everything
         and are immune to any parent overflow:hidden or transforms */
      items.forEach(function(item) {
        document.body.appendChild(item);
        gsap.set(item, {
          position:      'fixed',
          top:           0,
          left:          0,
          xPercent:      -50,
          yPercent:      -50,
          opacity:       0,
          scale:         0.82,
          pointerEvents: 'none',
          zIndex:        9990
        });
      });

      /* Listen on the whole section not just canvas —
         canvas may not fill the visible area reliably */
      section.addEventListener('mousemove', function(e) {
        var mx = e.clientX;
        var my = e.clientY;

        var dist = Math.sqrt(Math.pow(mx - lastX, 2) + Math.pow(my - lastY, 2));
        if (dist < threshold) return;
        lastX = mx;
        lastY = my;

        var item = items[activeIndex % totalItems];
        activeIndex++;
        zCounter++;

        var rot = (Math.random() - 0.5) * 24;

        gsap.killTweensOf(item);
        gsap.set(item, {
          x:        mx,
          y:        my,
          rotation: rot,
          zIndex:   zCounter,
          scale:    0.82,
          opacity:  1
        });

        gsap.to(item, {
          scale:    1,
          duration: 0.5,
          ease:     'power3.out'
        });

        gsap.to(item, {
          opacity:  0,
          scale:    0.88,
          duration: 0.55,
          delay:    0.5,
          ease:     'power2.in'
        });
      });

      section.addEventListener('mouseleave', function() {
        gsap.to(items, { opacity: 0, duration: 0.3, overwrite: true });
      });
    }

    /* ── MOBILE: 3D coverflow carousel ──
       Cards arranged in a perspective arc — active card is
       front-centre, neighbours recede with rotateY + scale + translateZ.
       Swipe left/right to cycle. GSAP handles all transitions. */
    var carousel = section.querySelector('[data-trail-carousel]');
    var slides   = section.querySelectorAll('[data-trail-slide]');
    var dots     = section.querySelectorAll('.img-trail__dot');

    if (!carousel || !slides.length || window.innerWidth >= 768) return;

    var total   = slides.length;
    var current = 0;
    var startX  = 0;
    var dragX   = 0;
    var dragging = false;

    /* Perspective config */
    var SPREAD_X   = 75;   /* px between cards horizontally */
    var ROTATE_Y   = 38;   /* deg rotation for off-centre cards */
    var SCALE_OFF  = 0.72; /* scale of non-active cards */
    var TRANS_Z    = -140; /* translateZ for receding cards */
    var OPACITY_OFF = 0.45;

    /* Set perspective on the carousel wrapper */
    gsap.set(carousel, { perspective: 900, perspectiveOrigin: '50% 50%' });

    /* Remove flex layout — we'll position cards absolutely in 3D space */
    var track = section.querySelector('[data-trail-track]');
    if (track) {
      gsap.set(track, {
        position: 'relative',
        width: '100%',
        height: '100%',
        transformStyle: 'preserve-3d'
      });
    }

    /* Position all slides absolutely centred */
    slides.forEach(function(slide) {
      gsap.set(slide, {
        position:  'absolute',
        top:       '50%',
        left:      '50%',
        xPercent:  -50,
        yPercent:  -50,
        transformStyle: 'preserve-3d'
      });
    });

    function layout(animate) {
      slides.forEach(function(slide, i) {
        /* Offset from current — wrap around */
        var offset = i - current;
        /* Clamp offset for visuals — cards beyond ±2 hide off-screen */
        var clamp  = Math.max(-3, Math.min(3, offset));

        var tx      = clamp * SPREAD_X;
        var ry      = clamp * -ROTATE_Y;
        var scale   = clamp === 0 ? 1 : SCALE_OFF - Math.abs(clamp) * 0.04;
        var tz      = clamp === 0 ? 0 : TRANS_Z - Math.abs(clamp) * 20;
        var opacity = clamp === 0 ? 1 : OPACITY_OFF - Math.abs(clamp) * 0.08;
        var zIndex  = 10 - Math.abs(clamp);

        var props = {
          x:         tx,
          rotateY:   ry,
          scale:     Math.max(0.3, scale),
          z:         tz,
          opacity:   Math.max(0, opacity),
          zIndex:    zIndex,
          duration:  animate ? 0.55 : 0,
          ease:      'power3.out',
          overwrite: true
        };

        if (animate) {
          gsap.to(slide, props);
        } else {
          gsap.set(slide, props);
        }
      });

      /* Dots */
      dots.forEach(function(dot, i) {
        dot.classList.toggle('active', i === current);
      });
    }

    function goTo(idx) {
      current = ((idx % total) + total) % total;
      layout(true);
    }

    /* Touch / drag — stopPropagation so section trail listener
       doesn't also fire and conflict */
    carousel.addEventListener('touchstart', function(e) {
      startX   = e.touches[0].clientX;
      dragging = true;
      e.stopPropagation();
    }, { passive: true });

    carousel.addEventListener('touchmove', function(e) {
      if (!dragging) return;
      e.stopPropagation();
      dragX = e.touches[0].clientX - startX;
      /* Live drag — shift cards slightly with drag */
      slides.forEach(function(slide, i) {
        var offset = i - current;
        var clamp  = Math.max(-3, Math.min(3, offset));
        gsap.set(slide, { x: clamp * SPREAD_X + dragX * 0.25 });
      });
    }, { passive: true });

    carousel.addEventListener('touchend', function(e) {
      if (!dragging) return;
      dragging = false;
      e.stopPropagation();
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) {
        goTo(dx < 0 ? current + 1 : current - 1);
      } else {
        layout(true); /* snap back */
      }
      dragX = 0;
    });

    /* Stagger slides in on scroll enter then settle into arc */
    ScrollTrigger.create({
      trigger: section, start: 'top 85%', once: true,
      onEnter: function() {
        /* Start all off-screen right, stagger in, then layout */
        slides.forEach(function(slide, i) {
          gsap.set(slide, { opacity: 0, x: 120, rotateY: -20, scale: 0.7 });
          gsap.to(slide, {
            opacity: 1, x: 0, rotateY: 0, scale: 1,
            duration: 0.7, delay: i * 0.07, ease: 'power3.out',
            onComplete: i === total - 1 ? function() { layout(true); } : null
          });
        });
      }
    });

    /* Initial layout without animation */
    layout(false);
  }


  /* ═══════════════════════════════════════════════════════════
     PRODUCT PAGE — Entrance, sticky ATC, thumb crossfade,
                    variant pulse, meta stagger
     Performance notes:
     • Only transform + opacity animated (no layout props)
     • will-change removed after animation via onComplete
     • IntersectionObserver for sticky ATC (not scroll listener)
     • Images preloaded before crossfade swap
  ═══════════════════════════════════════════════════════════ */
  function initProductPage() {
    var productMain = document.querySelector('.product-main');
    if (!productMain) return;

    var gallery      = productMain.querySelector('[data-product-gallery]');
    var imgWrap      = productMain.querySelector('[data-product-main-img-wrap]');
    var featuredImg  = productMain.querySelector('[data-product-featured-img]');
    var infoCol      = productMain.querySelector('[data-product-info]');
    var titleEl      = productMain.querySelector('[data-product-title]');
    var priceEl      = productMain.querySelector('[data-product-price]');
    var atcRow       = productMain.querySelector('[data-product-atc-row]');
    var metaStrip    = productMain.querySelector('[data-product-meta]');
    var collTag      = productMain.querySelector('.product-collection-tag');
    var options      = productMain.querySelectorAll('.product-option');
    var thumbs       = productMain.querySelectorAll('[data-product-thumb]');
    var breadcrumb   = productMain.querySelector('.breadcrumbs');

    /* ── 1. PAGE ENTRANCE ──────────────────────────────── */
    var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    /* Breadcrumb fades in first */
    if (breadcrumb) {
      gsap.set(breadcrumb, { opacity: 0, y: -8 });
      tl.to(breadcrumb, { opacity: 1, y: 0, duration: 0.4 }, 0);
    }

    /* Gallery — clip-path wipes up from bottom */
    if (imgWrap) {
      gsap.set(imgWrap, { clipPath: 'inset(0 0 100% 0)' });
      tl.to(imgWrap, {
        clipPath: 'inset(0 0 0% 0)',
        duration: 0.85,
        ease: 'expo.inOut',
        onComplete: function() {
          gsap.set(imgWrap, { clearProps: 'clipPath,willChange' });
        }
      }, 0.05);
    }

    /* Thumbs stagger up */
    var thumbRow = productMain.querySelector('.product-gallery__thumbs');
    if (thumbRow) {
      var thumbBtns = thumbRow.querySelectorAll('.product-gallery__thumb');
      gsap.set(thumbBtns, { opacity: 0, y: 12 });
      tl.to(thumbBtns, {
        opacity: 1, y: 0,
        duration: 0.45, stagger: 0.06, ease: 'power3.out'
      }, 0.7);
    }

    /* Info column — elements arrive in sequence */
    if (collTag) {
      gsap.set(collTag, { opacity: 0, x: -16 });
      tl.to(collTag, { opacity: 1, x: 0, duration: 0.4 }, 0.3);
    }

    /* Title — word slam */
    if (titleEl && hasSplitText) {
      gsap.set(titleEl, { opacity: 1 });
      var titleSplit = new SplitText(titleEl, { type: 'words', wordsClass: 'pt-word' });
      titleSplit.words.forEach(function(w) {
        var mask = document.createElement('span');
        mask.style.cssText = 'display:inline-block;overflow:hidden;vertical-align:bottom;padding-bottom:0.04em;';
        w.parentNode.insertBefore(mask, w);
        mask.appendChild(w);
      });
      gsap.set(titleSplit.words, { yPercent: 110 });
      tl.to(titleSplit.words, {
        yPercent: 0, duration: 0.6, stagger: 0.04, ease: 'expo.out',
        onComplete: function() { gsap.set(titleSplit.words, { clearProps: 'willChange' }); }
      }, 0.35);
    } else if (titleEl) {
      gsap.set(titleEl, { opacity: 0, y: 20 });
      tl.to(titleEl, { opacity: 1, y: 0, duration: 0.6 }, 0.35);
    }

    /* Price fades in with a slight upward drift */
    if (priceEl) {
      gsap.set(priceEl, { opacity: 0, y: 10 });
      tl.to(priceEl, { opacity: 1, y: 0, duration: 0.45 }, 0.55);
    }

    /* Options stagger in */
    if (options.length) {
      gsap.set(options, { opacity: 0, y: 14 });
      tl.to(options, { opacity: 1, y: 0, duration: 0.4, stagger: 0.08 }, 0.65);
    }

    /* ATC row rises last */
    if (atcRow) {
      gsap.set(atcRow, { opacity: 0, y: 16 });
      tl.to(atcRow, { opacity: 1, y: 0, duration: 0.5, ease: 'back.out(1.3)' }, 0.75);
    }

    /* Gold rule under collection tag draws in */
    if (collTag) {
      var rule = collTag.querySelector('::before');
      /* Use the pseudo-element via CSS animation triggered by class */
      setTimeout(function() { collTag.classList.add('tag-rule-drawn'); }, 300);
    }

    /* ── 2. META STRIP — staggered scroll reveal ──────── */
    if (metaStrip) {
      var metaItems = metaStrip.querySelectorAll('span');
      gsap.set(metaItems, { opacity: 0, y: 12 });
      ScrollTrigger.create({
        trigger: metaStrip, start: 'top 92%', once: true,
        onEnter: function() {
          gsap.to(metaItems, {
            opacity: 1, y: 0,
            duration: 0.45, stagger: 0.1, ease: 'power3.out'
          });
        }
      });
    }

    /* ── 3. THUMBNAIL CROSSFADE ───────────────────────── */
    thumbs.forEach(function(thumb) {
      thumb.addEventListener('click', function() {
        var newSrc = thumb.getAttribute('data-img-src');
        if (!newSrc || !featuredImg) return;

        /* Update active state */
        thumbs.forEach(function(t) { t.classList.remove('active'); });
        thumb.classList.add('active');

        /* Preload the new image, then crossfade */
        var preload = new Image();
        preload.onload = function() {
          if (!imgWrap) {
            featuredImg.src = newSrc;
            return;
          }
          /* Clip-path wipe down then up */
          gsap.timeline()
            .to(featuredImg, {
              clipPath: 'inset(0 0 100% 0)',
              duration: 0.22,
              ease: 'power2.in',
              onComplete: function() {
                featuredImg.src = newSrc;
              }
            })
            .to(featuredImg, {
              clipPath: 'inset(0 0 0% 0)',
              duration: 0.35,
              ease: 'expo.out'
            });
        };
        preload.src = newSrc;
      });
    });

    /* ── 4. VARIANT SELECT PULSE ──────────────────────── */
    var selects = productMain.querySelectorAll('[data-option-position]');
    selects.forEach(function(select) {
      select.addEventListener('change', function() {
        if (!priceEl) return;
        priceEl.classList.remove('product-info__price--pulse');
        void priceEl.offsetWidth; /* reflow */
        priceEl.classList.add('product-info__price--pulse');
        /* Remove class after animation */
        setTimeout(function() {
          priceEl.classList.remove('product-info__price--pulse');
        }, 450);
      });
    });

    /* ── 5. STICKY ATC BAR ────────────────────────────── */
    var stickyBar   = document.getElementById('sticky-atc');
    var stickyBtn   = document.getElementById('sticky-atc-btn');
    var mainAtcBtn  = document.getElementById('atc-btn');

    if (stickyBar && mainAtcBtn) {
      var barVisible = false;

      /* IntersectionObserver — fires when main ATC button leaves viewport */
      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          var shouldShow = !entry.isIntersecting;
          if (shouldShow === barVisible) return;
          barVisible = shouldShow;

          if (shouldShow) {
            stickyBar.setAttribute('aria-hidden', 'false');
            gsap.to(stickyBar, {
              y: 0, duration: 0.45, ease: 'power3.out', overwrite: true
            });
          } else {
            gsap.to(stickyBar, {
              y: '100%', duration: 0.35, ease: 'power2.in', overwrite: true,
              onComplete: function() { stickyBar.setAttribute('aria-hidden', 'true'); }
            });
          }
        });
      }, { threshold: 0, rootMargin: '0px 0px -20px 0px' });

      observer.observe(mainAtcBtn);

      /* Sticky button submits the main form */
      if (stickyBtn) {
        stickyBtn.addEventListener('click', function() {
          /* Delegate to main ATC button */
          var mainForm = document.getElementById('product-form');
          if (mainForm) {
            /* Trigger the form's submit event so Ajax handler fires */
            mainForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
          /* Visual feedback on sticky button */
          var originalText = stickyBtn.textContent;
          stickyBtn.textContent = '✓ Added';
          stickyBtn.disabled = true;
          setTimeout(function() {
            stickyBtn.textContent = originalText;
            stickyBtn.disabled = false;
          }, 2500);
        });
      }
    }
  }

  /* ══════════════════════════════════════════════════════
     HERO ENTRANCE — fires after loader wipes away
     Extracted from init() so afterLoader() can defer it.
  ══════════════════════════════════════════════════════ */
  function initHeroEntrance() {
    var hero = document.querySelector('[data-hero]');
    if (!hero) return;

    var eyebrow    = hero.querySelector('[data-hero-eyebrow]');
    var headline   = hero.querySelector('[data-hero-headline]');
    var rule       = hero.querySelector('[data-hero-rule]');
    var sub        = hero.querySelector('[data-hero-sub]');
    var actions    = hero.querySelector('[data-hero-actions]');
    var circle     = hero.querySelector('[data-hero-circle]');
    var trustItems = hero.querySelectorAll('[data-trust-item]');
    var videoPanel = hero.querySelector('.hero-video-panel');

    var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    /* Circle breathes in */
    if (circle) {
      gsap.set(circle, { scale: 0.55, opacity: 0 });
      tl.to(circle, { scale: 1, opacity: 0.35, duration: 1.6, ease: 'power2.out' }, 0);
    }

    /* Background media wipes up via clip-path.
       The panel is already hidden via CSS clip-path from first paint —
       we just confirm the start state and animate to fully revealed. */
    if (videoPanel) {
      gsap.set(videoPanel, { clipPath: 'inset(100% 0 0 0)' });
      tl.to(videoPanel, { clipPath: 'inset(0% 0 0 0)', duration: 1.1, ease: 'expo.inOut' }, 0);
    }

    /* Eyebrow */
    if (eyebrow) {
      gsap.set(eyebrow, { opacity: 0, x: -18 });
      tl.to(eyebrow, { opacity: 1, x: 0, duration: 0.6 }, 0.3);
    }

    /* Headline — line-by-line */
    if (headline) {
      if (hasSplitText) {
        gsap.set(headline, { opacity: 1 });
        var split = new SplitText(headline, { type: 'lines', linesClass: 'split-line' });
        split.lines.forEach(function(line) {
          var wrap = document.createElement('div');
          wrap.style.cssText = 'overflow:hidden;display:block;';
          line.parentNode.insertBefore(wrap, line);
          wrap.appendChild(line);
        });
        gsap.set(split.lines, { yPercent: 110, opacity: 0 });
        tl.to(split.lines, {
          yPercent: 0, opacity: 1,
          duration: 0.85, stagger: 0.18, ease: 'power3.out'
        }, 0.5);
      } else {
        gsap.set(headline, { opacity: 0, y: 28 });
        tl.to(headline, { opacity: 1, y: 0, duration: 0.85 }, 0.5);
      }
    }

    if (rule) {
      gsap.set(rule, { width: 0, opacity: 0 });
      tl.to(rule, { width: 'clamp(40px,6vw,64px)', opacity: 1, duration: 0.6, ease: 'power2.inOut' }, 1.1);
    }

    if (sub) {
      gsap.set(sub, { opacity: 0, y: 16 });
      tl.to(sub, { opacity: 1, y: 0, duration: 0.6 }, 1.25);
    }

    if (actions) {
      var btns = actions.querySelectorAll('a');
      gsap.set(btns, { opacity: 0, y: 14 });
      tl.to(btns, { opacity: 1, y: 0, duration: 0.5, stagger: 0.1 }, 1.4);
    }

    if (trustItems.length) {
      gsap.set(trustItems, { opacity: 0, y: 20 });
      tl.to(trustItems, { opacity: 1, y: 0, duration: 0.5, stagger: 0.08, ease: 'power2.out' }, 1.5);
    }

    var bgLine = hero.querySelector('.hero-bg__line--1');
    if (bgLine) {
      gsap.fromTo(bgLine,
        { scaleY: 0, transformOrigin: 'top center', opacity: 0 },
        { scaleY: 1, opacity: 0.6, duration: 1.4, delay: 0.15, ease: 'power2.out' }
      );
    }

    /* ── Hero video: pause when scrolled off-screen ─────────── */
    var heroVideo = hero.querySelector('.hero-video');
    if (heroVideo && 'IntersectionObserver' in window) {
      var heroVidObs = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            heroVideo.play().catch(function() {});
          } else {
            heroVideo.pause();
          }
        });
      }, { threshold: 0.1 });
      heroVidObs.observe(hero);
    }
  }


  /* ═══════════════════════════════════════════════════════════
     COLLECTION PAGE — hero entrance + product grid stagger
  ═══════════════════════════════════════════════════════════ */
  function initCollectionPage() {
    var hero = document.querySelector('[data-coll-hero]');
    var grid = document.querySelector('[data-coll-grid]');
    if (!hero && !grid) return;

    /* ── HERO ENTRANCE — deferred until loader wipes ── */
    if (hero) {
      afterLoader(function() {
      var eyebrow  = hero.querySelector('[data-ch-eyebrow]');
      var titleEl  = hero.querySelector('[data-ch-title]');
      var descEl   = hero.querySelector('[data-ch-desc]');
      var rule     = hero.querySelector('[data-ch-rule]');
      var countNum = hero.querySelector('[data-ch-count-num]');
      var countLbl = hero.querySelector('[data-ch-count-label]');
      var breadcrumb = hero.querySelector('.breadcrumbs');

      var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      /* Breadcrumb */
      if (breadcrumb) {
        gsap.set(breadcrumb, { opacity: 0, y: -8 });
        tl.to(breadcrumb, { opacity: 1, y: 0, duration: 0.4 }, 0);
      }

      /* Eyebrow line draws then text fades */
      if (eyebrow) {
        gsap.set(eyebrow, { opacity: 0, x: -20 });
        tl.to(eyebrow, { opacity: 1, x: 0, duration: 0.45 }, 0.1);
      }

      /* Title — word slam */
      if (titleEl && hasSplitText) {
        gsap.set(titleEl, { opacity: 1 });
        var ts = new SplitText(titleEl, { type: 'words', wordsClass: 'ch-word' });
        ts.words.forEach(function(w) {
          var mask = document.createElement('span');
          mask.style.cssText = 'display:inline-block;overflow:hidden;vertical-align:bottom;padding-bottom:0.04em;';
          w.parentNode.insertBefore(mask, w);
          mask.appendChild(w);
        });
        gsap.set(ts.words, { yPercent: 110 });
        tl.to(ts.words, {
          yPercent: 0, duration: 0.65, stagger: 0.05, ease: 'expo.out'
        }, 0.2);
      } else if (titleEl) {
        gsap.set(titleEl, { opacity: 0, y: 24 });
        tl.to(titleEl, { opacity: 1, y: 0, duration: 0.65 }, 0.2);
      }

      /* Description fades up */
      if (descEl) {
        gsap.set(descEl, { opacity: 0, y: 14 });
        tl.to(descEl, { opacity: 1, y: 0, duration: 0.5 }, 0.55);
      }

      /* Rule draws left to right */
      if (rule) {
        gsap.set(rule, { scaleX: 0, transformOrigin: 'left center' });
        tl.to(rule, { scaleX: 1, duration: 0.7, ease: 'expo.out' }, 0.6);
      }

      /* Product count — number counts up */
      if (countNum) {
        var target  = parseInt(countNum.getAttribute('data-count') || countNum.textContent, 10);
        var display = countNum.textContent;
        gsap.set(countNum,  { opacity: 0, y: 20 });
        if (countLbl) gsap.set(countLbl, { opacity: 0 });

        tl.to(countNum, { opacity: 1, y: 0, duration: 0.5 }, 0.45);
        if (countLbl) tl.to(countLbl, { opacity: 1, duration: 0.35 }, 0.6);

        /* Count up animation */
        if (!isNaN(target) && target > 0) {
          tl.call(function() {
            var obj = { val: 0 };
            gsap.to(obj, {
              val: target, duration: 1.0, ease: 'power2.out',
              onUpdate: function() {
                countNum.textContent = Math.round(obj.val);
              },
              onComplete: function() {
                countNum.textContent = display;
              }
            });
          }, null, 0.45);
        }
      }
      }); /* end afterLoader */
    }

    /* ── PRODUCT GRID — staggered clip-path reveal ── */
    if (grid) {
      var cards = grid.querySelectorAll('[data-product-card]');
      if (!cards.length) return;

      /* Cards start clipped from below */
      gsap.set(cards, { opacity: 0, y: 30 });

      /* Use a single ScrollTrigger per row batch for performance
         Row = floor(index / columns). Cards in the same row fire together. */
      var columns = window.innerWidth >= 1024 ? 3 : window.innerWidth >= 600 ? 2 : 1;

      cards.forEach(function(card, i) {
        var row         = Math.floor(i / columns);
        var colPosition = i % columns;
        var rowDelay    = 0; /* each card fires when it enters viewport */

        ScrollTrigger.create({
          trigger: card,
          start:   'top 90%',
          once:    true,
          onEnter: function() {
            gsap.to(card, {
              opacity:  1,
              y:        0,
              duration: 0.55,
              delay:    colPosition * 0.08, /* stagger within the row */
              ease:     'power3.out'
            });
          }
        });
      });

      /* Image hover — cycle through product images as cursor moves across card */
      if (!isMobile) {
        cards.forEach(function(card) {
          var imgWrap = card.querySelector('[data-card-img-wrap]');
          if (!imgWrap) return;

          var imgs = imgWrap.querySelectorAll('[data-card-img]');
          var dots = card.querySelectorAll('.product-card__dot');
          if (!imgs.length) return;

          var current  = 0;
          var total    = imgs.length;

          /* Set all alt images hidden */
          imgs.forEach(function(img, i) {
            if (i > 0) gsap.set(img, { opacity: 0 });
          });

          if (total <= 1) {
            /* Single image — just scale on hover */
            card.addEventListener('mouseenter', function() {
              gsap.to(imgs[0], { scale: 1.04, duration: 0.6, ease: 'power2.out' });
            });
            card.addEventListener('mouseleave', function() {
              gsap.to(imgs[0], { scale: 1, duration: 0.5, ease: 'power2.out' });
            });
            return;
          }

          /* Multi image — divide card width into zones, each zone = one image */
          function showImage(idx) {
            if (idx === current) return;
            var prev = current;
            current  = idx;

            gsap.to(imgs[prev], { opacity: 0, duration: 0.25, ease: 'power2.out' });
            gsap.to(imgs[current], { opacity: 1, duration: 0.25, ease: 'power2.out' });

            /* Update dots */
            dots.forEach(function(dot, i) {
              dot.classList.toggle('active', i === current);
            });
          }

          imgWrap.addEventListener('mousemove', function(e) {
            var rect   = imgWrap.getBoundingClientRect();
            var pct    = (e.clientX - rect.left) / rect.width;
            var idx    = Math.min(total - 1, Math.floor(pct * total));
            showImage(idx);
          });

          imgWrap.addEventListener('mouseleave', function() {
            /* Reset to first image */
            showImage(0);
            /* Scale back */
            imgs.forEach(function(img) {
              gsap.to(img, { scale: 1, duration: 0.4, ease: 'power2.out' });
            });
          });

          imgWrap.addEventListener('mouseenter', function() {
            gsap.to(imgs[current], { scale: 1.04, duration: 0.6, ease: 'power2.out' });
          });
        });
      }
    }
  }

  function initAll() {
    initScrollSmoother();
    initPageLoader();
    initPageTransitions();
    init();
    initAboutHero();
    initStoryReveal();
    initImageTrail();
    afterLoader(initProductPage);
    initCollectionPage();
  }

  function tryInit() {
    if (
      typeof gsap === 'undefined' ||
      typeof ScrollTrigger === 'undefined' ||
      typeof SplitText === 'undefined' ||
      (typeof ScrollSmoother === 'undefined' && !isMobile)
    ) {
      setTimeout(tryInit, 50);
      return;
    }
    initAll();
  }

  /* Safety net — if GSAP fails to load within 3s (CDN down, slow connection)
     hide the loader anyway so the site isn't permanently stuck */
  setTimeout(function() {
    if (!loaderDone) {
      var loader = document.getElementById('tcc-loader');
      if (loader) { loader.style.display = 'none'; loader.style.transform = ''; }
      document.documentElement.classList.add('tcc-loaded');
      document.body.style.visibility = 'visible';
      document.body.style.overflow = '';
      document.body.classList.add('tcc-loaded');
      document.body.classList.add('tcc-hero-ready');
      document.body.classList.add('gsap-ready');
      loaderDone = true;
      revealAll();
    }
  }, 3000);

  if (document.readyState === 'complete') {
    tryInit();
  } else {
    window.addEventListener('load', tryInit);
  }

})();